import { Injectable } from '@angular/core';
import { Observable, combineLatest, of, asyncScheduler, EMPTY } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { switchMap, map, debounceTime, concatMap, take, refCount, publishReplay, pluck, throttleTime, startWith, catchError } from 'rxjs/operators';

import { UserService } from './user.service';
import { ProjectService, ProjectChangeEv } from './project.service';
import { MoteService } from './mote.service';
import { PositionService } from './position.service';

import { User } from '../models/user';
import { Mote } from '../models/mote';
import { Coords } from '../models/coords';
import { UserPositionService } from './user-position.service';
import { Project, MoteSortType, MoteEventType, MoteEvent, MoteState } from '../models';
import { MoteEventService } from './mote-event.service';

export interface UserProjectEv { pos: Coords, user: User, motes: Mote[], project: Project };
export interface UserTourEv { pos: Coords, user: User, tour: Mote, stories: Mote[], project: Project };

@Injectable({ providedIn: 'root' })
export class UserMoteService {
    private sharedProjectObs: Observable<UserProjectEv> = null;
    private sharedTourObs: Observable<UserTourEv> = null;

    constructor(
        private userService: UserService,
        private moteService: MoteService,
        private userPositionService: UserPositionService,
        private positionService: PositionService,
        private projectService: ProjectService,
        private moteEventService: MoteEventService) {
    }

    /**
    * The motes observable to rule them all.
    * Subscribe to motes of active project and update states/distances according to userRights and position
    */
    public watchUserProjectMotes(showDeleted: boolean = false): Observable<UserProjectEv> {
        // Create a shared observer (which only calls map() one time er update for all subscribers)
        if(!this.sharedProjectObs) this.sharedProjectObs = this.watchProjectMotes(showDeleted).pipe(publishReplay(1), refCount());
        return this.sharedProjectObs;
    }

    /**
    * Subscribe to active tour and its stories, and update states/distances according to userRights and position
    */
    public watchUserTourMotes(): Observable<UserTourEv> {
        // Create a shared observer (which only calls map() one time er update for all subscribers)
        if(!this.sharedTourObs) this.sharedTourObs = this.watchTourMotes().pipe(shareReplay(1));
        return this.sharedTourObs;
    }

    public getUserProjectMotes(): Promise<UserProjectEv> {
        return this.watchUserProjectMotes().pipe(take(1)).toPromise();
    }

    public getUserTourMotes(): Promise<UserTourEv> {
        return this.watchUserTourMotes().pipe(take(1)).toPromise();
    }

    /**
     * Watch motes of the active project or motes from the project with the given key and augment user data 
     */
    public watchProjectMotes(showDeleted: boolean = false, projectKey: string = ""): Observable<UserProjectEv> {
        let watchUser = this.userService.watchCurrentUser(); //.pipe(tap( (user) => { console.log("User changed!!! ", user.activeRoute)}));
        let watchProject = (projectKey) ? this.watchStaticProject(projectKey) : this.projectService.watchActiveProject(); // .pipe( tap( project => console.log("Project changed ", project.project.activeRouteKey )));
        let watchProjectKey = watchProject.pipe(pluck('project'), pluck('key')); //.pipe(tap( () => { console.log("Project changed!!!")}));

        // Motes of active project (should be distinct until project or project route key changes
        let watchMotes = watchProjectKey.pipe(switchMap(projectKey => this.moteService.watchProjectMotes([projectKey], showDeleted))); //.pipe(tap( () => { console.log("Motes changed!!!")}));
        let watchAugmentedMotes = this.augmentUserMotes((user: User) => watchMotes);
        let watchUpdateEvents = this.moteEventService.watchEventType(MoteEventType.MOTE_FILTER_UPDATED);
        // As user always changes on project change, it is save to zip all of them (to avoid multiple responses on same project change)
        // let watchUserProjectMotes = zip(watchUser, watchMotes, watchProject);
        // Combine latest (we can't use zip as watchMotes only fires on project change, not on route change)
        let watchUserProjectMotes = combineLatest([watchUser, watchAugmentedMotes, watchProject, watchUpdateEvents.pipe(startWith(null))]);

        // Subscribe to project, mote, position and user
        return watchUserProjectMotes.pipe(debounceTime(100), map((ev: [User, Mote[], ProjectChangeEv, MoteEvent]) => {
            // Expand event tuple
            let [user, motes, projectEv] = ev;
            let project = projectEv.project;
            // Get latest position directly from service in order to save a subscrption to this fast observable (augmented already subscribes to GPS vi watchUserPosition())
            let pos = this.positionService.lastKnownPosition;
            return { 'pos': pos, 'user': user, 'motes': motes, 'project': project };
        }));
    }

    /**
     * Watch user, active project, tour and stories
     */
    public watchTourMotes(projectKey: string = ""): Observable<UserTourEv> {
        // Create observables
        const watchUser = this.userService.watchCurrentUser();
        const watchProjectEv = (projectKey) ? this.watchStaticProject(projectKey) : this.projectService.watchActiveProject();
        const watchTourKey = watchProjectEv.pipe(pluck('project'), pluck('activeRouteKey'));
        const watchPreviewKey = watchProjectEv.pipe(pluck('project'), pluck('activePreviewKey'));
        const watchMotes = combineLatest([watchTourKey, watchPreviewKey]).pipe(switchMap(([tourKey, previewKey]) => this.watchTourAndStories(tourKey, previewKey)));

        let watchUserTourMotes = combineLatest([watchUser, watchMotes, watchProjectEv]);

        // Subscribe to project, mote, position and user
        return watchUserTourMotes.pipe(debounceTime(100), map((ev: [User, { tour: Mote, stories: Mote[] }, ProjectChangeEv]) => {
            // Expand event tuple
            let [user, motes, projectEv] = ev;
            let project = projectEv.project;
            // Get latest position directly from service in order to save a subscrption to this fast observable (augmented already subscribes to GPS vi watchUserPosition())
            let pos = this.positionService.lastKnownPosition;
            return { 'pos': pos, 'user': user, 'tour': motes.tour, 'stories': motes.stories, 'project': project };
        }));
    }

     /**
     * Watch tour and its children if user has permission.
     * Augment stories.
     */
      public watchTourAndStories(tourKey: string, previewKey: string) : Observable<{ tour: Mote, stories: Mote[] }> {
        let fetchedTour: Mote;

        return this.moteService.watchMotePreview(tourKey, previewKey).pipe(switchMap((tour) => {
            fetchedTour = tour;
            if(!fetchedTour) return of([]);
            // Augment stories
            const storiesObs = this.moteService.watchChildren(tour, true);
            return this.augmentUserMotes(() => storiesObs);
        }), map((stories) => {
            return { tour: fetchedTour, stories };
        }), catchError((err) => {
            // If tour does not exist or user does not have permission to access it
            return EMPTY;
        }))
    }

    /**
     * Watch static project and return observer
     */
    private watchStaticProject(projectKey: string): Observable<ProjectChangeEv> {
        return this.projectService.watchProject(projectKey).pipe(map(project => {
            return {
                project: project,
                tour: null,
                projectChanged: false,
                routeChanged: false
            }
        }));
    }

    /**
    *  Get a single mote and augment with user/gps state data
    */
    public getUserMote(moteKey: string): Promise<Mote> {
        return this.watchUserMote(moteKey).pipe(take(1)).toPromise();
    }

    /**
    *  Watch a mote and augment with user/gps state data
    */
    public watchUserMote(moteKey: string): Observable<Mote> {
        // Return fast, fast if mote is already in cache (mote is updated in batch and changes notified from list)
        if(Mote.moteCache[moteKey]) return of(Mote.moteCache[moteKey]);

        let watchMote = (user: User) => this.moteService.watchMotes([moteKey]);
        // Return mote observable with augmented state of single mote
        return this.augmentUserMotes(watchMote).pipe(concatMap(mote => mote));
    }

    /**
    *  Watch user voted motes
    */
    public watchLovedMotes(): Observable<Mote[]> {
        // Watch motes loved by user
        let watchMotes = (user: User) => this.moteService.watchMotes(user ? user.getLovedKeys() : []);
        // Return mote observable with augmented states
        return this.augmentUserMotes(watchMotes);
    }

    /**
    * Watch motes in route or collection of the active project or in the given project and augment them with user/gps state data
    * @param projectKey 
    */
    public watchMotesInCollection(collectionKey: string, sortBy: MoteSortType = MoteSortType.ORDER, projectKey: string = ""): Observable<Mote[]> {
        // Subscribe to collections (motes) of this world
        return this.watchProjectMotes(false, projectKey).pipe(map(ev => {
            // Get route motes
            let collectionMotes = ev.motes.filter(mote => mote.parent == collectionKey);
            // Sort motes in order
            return MoteService.sortMotes(collectionMotes, sortBy);
        }));
    }

    /**
    *  Watch user unlocked motes
    */
    public watchUnlockedMotes(): Observable<Mote[]> {
        // Watch motes unlocked by user
        let watchMotes = (user: User) => this.moteService.watchMotes(user ? user.getUnlockedKeys() : []);
        // Return mote observable with augmented states
        return this.augmentUserMotes(watchMotes);
    }

    private augmentUserMotes(watchMotes: (user) => Observable<Mote[]>) {
        // Watch user and position updates
        let watchUserPos = this.userPositionService.watchUserPosition().pipe(throttleTime(3000, asyncScheduler, { leading: true, trailing: true }));

        return watchUserPos.pipe(switchMap(user => watchMotes(user).pipe(debounceTime(300), map((motes: Mote[]) => {
            // Update distance (based on cached user position if any)
            this.moteService.updateMoteDistances(motes, user.position);
            let activeRouteKey = this.projectService.activeProject ? this.projectService.activeProject.activeRouteKey : "";
            let exclusiveRouteMode = this.projectService.activeProject ? this.projectService.activeProject.exclusiveRouteMode : true;
            // Update mote states (update liked, distance etc.)
            this.moteService.updateMoteStates(motes, user, activeRouteKey, exclusiveRouteMode, true);
            return motes;
        }))));
    }

    public async getNextMote(mote: Mote): Promise<Mote> {
        // Only show next mote for motes that has a parent (for now this is place and event motes)
        if(!mote || !mote.parent) return null;
        const parent = await this.moteService.getTour(mote.parent);
        if(!parent) return null;
        const children = await this.moteService.getChildren(parent);
        const sortedChildren  = MoteService.sortMotes(children, parent.sortBy);
        // Find the next mote depending on type of parent
        return (parent.sortBy == MoteSortType.DISTANCE) ? this.findClosest(sortedChildren, mote) : this.findNextInOrder(sortedChildren, mote);
    }

    /**
     * Get latest unlocked story of a tour, or first story if all are locked
     */
    public async getCurrentStory(parent: Mote): Promise<Mote> {
        if(!this.userService.currentUser) return;

        const unlockedTourMotes = this.userService.currentUser.unlockedMotes.filter((mote) => mote.tourKey == parent.key);
        const children = await this.moteService.getChildren(parent);
        if(children.length <= 0) return null;

        // If no stories are unlocked, return first
        if(unlockedTourMotes.length <= 0) {
            const sortedChildren  = MoteService.sortMotes(children, parent.sortBy);
            return sortedChildren[0];
        }

        // Otherwise, extract the latest unlocked story
        const sortedUnlocks = unlockedTourMotes.sort((u1, u2) => u2.createdOn - u1.createdOn);
        const latestUnlock = sortedUnlocks[0];
        return children.find((child) => child.key == latestUnlock.moteKey) || children[0];
    }

    /**
     * Find the next mote in the list of motes sorted by predefined mote order
     */
    private findNextInOrder(sortedMotes: Mote[], mote: Mote): Mote {
        // Find the next index in list (+1)
        let nextIdx = sortedMotes.findIndex((findMote => mote.key == findMote.key)) + 1;
        return (sortedMotes.length > nextIdx) ? sortedMotes[nextIdx] : null;
    }

    /**
     * Find the closest non-unlocked (locked) mote (sorted by distance) or closest mote unlocked if there are no more locked in collection
     */
    private findClosest(sortedMotes: Mote[], currentMote: Mote): Mote {
        // Find the closest locked mote, and make sure it is not the current one, since unlocking might be underway
        let closestLocked = sortedMotes.find((mote) => (mote.moteState != MoteState.UNLOCKED) && (mote.key != currentMote.key));
        // Find the closest already unlocked if there are no locked around
        return closestLocked ? closestLocked : sortedMotes.find((mote) => mote.key != currentMote.key);
    }
}
